<template>
  <div style="background-color:black;">
    <div>
      <!-- Header Section -->
      <v-row class="pa-2">
        <v-col cols="12" class="d-flex justify-space-between align-center">
          <!-- Back Button -->
          <v-btn icon class="back-button" @click="confirmBack">
            <v-icon class="white--text">mdi-arrow-left</v-icon>
          </v-btn>
          <!-- Add Quote Button (Hidden for Salesperson or if not permitted) -->
          <v-btn
            v-if="(isTechnician || !isSalesperson) && hasQuoteEditPermission"
            color="#ff3b47"
            width="150px"
            dense
            class="add-job-button my-6"
            dark
            @click="handleAddQuoteClick"
          >
            Add Quote
          </v-btn>
        </v-col>
      </v-row>

      <!-- (Header removed so cards move up) -->

      <div style="background-color:white; border-radius:20px 20px 0 0; min-height:70%;">
        <!-- Search and Filter Section -->
        <v-row justify="center">
          <v-col cols="12" md="4">
            <v-text-field
              class="pa-3 white-search-field"
              filled
              dense
              rounded
              v-model="search"
              append-icon="mdi-magnify"
              label="Search Quotes"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <!-- Filter by Lead Time Dropdown -->
            <v-select
              v-model="selectedLeadTime"
              :items="leadTimeFilterOptions"
              placeholder="Filter by Lead Time"
              filled
              dense
              rounded
              class="my-3"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Quotes Display Section -->
        <v-row>
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            v-for="(quote, index) in paginatedItems"
            :key="index"
          >
            <v-card class="modern-card" elevation="6" hover>
              <!-- Header with Lead Time, Quote Number, Info Button, and Copy Content Button -->
              <v-row class="d-flex justify-end pr-3 pt-3 align-center">
                <!-- Lead Time Chip (if available on the first stock item) -->
                <v-chip
                  v-if="quote.stockItems && quote.stockItems.length && quote.stockItems[0].leadTime"
                  :color="getChipColor(quote.stockItems[0].leadTime)"
                  dark
                  label
                  class="status-chip ml-2"
                >
                  {{ quote.stockItems[0].leadTime }}
                </v-chip>
                <!-- Quote Number -->
                <v-chip color="grey" dark label class="quote-number-chip ml-2">
                  {{ quote.quoteNumber || "N/A" }}
                </v-chip>
                <!-- Info Button -->
                <v-btn
                  icon
                  @click="openExtraInfo(quote)"
                  title="Extra Info"
                  class="ml-2"
                >
                  <v-icon color="#AC1726">mdi-information</v-icon>
                </v-btn>
              </v-row>
              <!-- Card Content -->
              <v-card-text class="pa-4">
                <v-row>
                  <v-col>
                    <span class="text-caption">Customer Name:</span>
                    <span class="font-weight-medium">{{ quote.customerName || "N/A" }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <span class="text-caption">Seal Type:</span>
                    <span class="font-weight-medium">{{ quote.jobName || "N/A" }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <span class="text-caption">Shaft Size:</span>
                    <span class="font-weight-medium">{{ quote.ShaftSize || "N/A" }}</span>
                  </v-col>
                </v-row>
                <!-- Days Since Quoted -->
                <v-row>
                  <v-col>
                    <span class="text-caption">Days Since Quoted:</span>
                    <span class="font-weight-medium">
                      {{ computeDaysSinceQuoted(quote.currentDate) }}
                    </span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <span class="text-caption">Grand Total:</span>
                    <span class="font-weight-medium">{{ quote.grandTotal || "0.00" }}</span>
                  </v-col>
                </v-row>
              </v-card-text>
              <!-- Card Actions (only shown if permitted) -->
              <v-card-actions v-if="hasQuoteEditPermission">
                <v-btn
                  width="100%"
                  rounded
                  dark
                  color="#ff3b47"
                  @click.stop="navigateToEditQuote(quote)"
                >
                  Edit Quote
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- Load More Button -->
        <v-row justify="center" v-if="canLoadMore">
          <v-btn color="#ff3b47" @click="loadMore" :disabled="!canLoadMore">
            Load More
          </v-btn>
        </v-row>
      </div>

      <!-- Extra Info Dialog -->
      <v-dialog v-model="extraInfoDialog.visible" max-width="600px">
        <v-card class="extra-info-card">
          <v-card-title class="headline">
            <v-icon class="mr-2" color="#ff3b47">mdi-file-document-outline</v-icon>
            Extra Information
            <v-spacer/>
            <v-btn icon @click.stop="copyCardContent(extraInfo)" title="Copy Card Content">
              <v-icon color="#AC1726">mdi-content-copy</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-list dense>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Seal Type:</strong> {{ extraInfo.jobName || "N/A" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Shaft Size:</strong> {{ extraInfo.ShaftSize || "N/A" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="extraInfo.jobDescription && extraInfo.jobDescription !== 'N/A'">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Job Description:</strong> {{ extraInfo.jobDescription }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Customer Name:</strong> {{ extraInfo.customerName || "N/A" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Contact Person:</strong> {{ extraInfo.customerNumber || "N/A" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Customer Reference:</strong> {{ extraInfo.customerReference || "N/A" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Sample Number:</strong> {{ extraInfo.sampleNumber || "N/A" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <!-- Material Information from first stock item (if available) -->
              <v-list-item v-if="extraInfo.stockItems && extraInfo.stockItems.length">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Qty:</strong> {{ extraInfo.stockItems[0].quantity || "N/A" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="extraInfo.stockItems && extraInfo.stockItems.length">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Materials:</strong> {{ extraInfo.stockItems[0].code || "N/A" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="extraInfo.stockItems && extraInfo.stockItems.length">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Delivery Time:</strong> {{ extraInfo.stockItems[0].leadTime || "N/A" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="extraInfo.stockItems && extraInfo.stockItems.length">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>Price:</strong> {{ extraInfo.stockItems[0].totalPrice || "N/A" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="closeExtraInfoDialog">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <!-- Confirmation Dialog for Back Button -->
    <v-dialog v-model="backDialog" persistent max-width="400px">
      <v-card>
        <v-card-title class="text-h5">Confirm Navigation</v-card-title>
        <v-card-text>Are you sure you want to go back?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="backDialog = false">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="navigateToFunctions">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar Notification -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

export default {
  name: "QuotesList",
  data() {
    return {
      backDialog: false,
      quotes: [],
      search: "",
      // Using selectedLeadTime for filtering quotes by lead time
      selectedLeadTime: "Clear Filter",
      leadTimeFilterOptions: [
        "Inspection",
        "Drawing",
        "Quoted",
        "Completed",
        "Canceled",
        "Clear Filter",
      ],
      userRole: "",
      customers: [],
      visibleCount: 9,
      loadIncrement: 9,
      extraInfoDialog: {
        visible: false,
      },
      extraInfo: {},
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      // The userPermissions object for quotes is fetched with the user role
      userPermissions: {},
    };
  },
  computed: {
    isSalesperson() {
      return localStorage.getItem("role") === "Salesperson";
    },
    isTechnician() {
      return localStorage.getItem("role") === "Technician";
    },
    /**
     * Determines if the user has permission to add or edit quotes.
     * Returns true only if neither Add_Button nor Edit_Details is enabled.
     */
    hasQuoteEditPermission() {
      return !(
        this.userPermissions.Add_Button === true ||
        this.userPermissions.Edit_Details === true
      );
    },
    filteredQuotes() {
      let filtered = this.quotes;
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(
          (quote) =>
            (quote.jobName &&
              quote.jobName.toLowerCase().includes(searchLower)) ||
            (quote.quoteNumber &&
              quote.quoteNumber.toString().toLowerCase().includes(searchLower)) ||
            (quote.customerName &&
              quote.customerName.toLowerCase().includes(searchLower))
        );
      }
      if (this.selectedLeadTime && this.selectedLeadTime !== "Clear Filter") {
        filtered = filtered.filter((quote) => {
          return (
            quote.stockItems &&
            quote.stockItems.length &&
            quote.stockItems[0].leadTime === this.selectedLeadTime
          );
        });
      }
      filtered.sort((a, b) => b.quoteNumber - a.quoteNumber);
      return filtered;
    },
    paginatedItems() {
      return this.filteredQuotes.slice(0, this.visibleCount);
    },
    canLoadMore() {
      return this.visibleCount < this.filteredQuotes.length;
    },
  },
  watch: {
    search() {
      this.resetPagination();
    },
    selectedLeadTime() {
      this.resetPagination();
    },
  },
  async created() {
    await this.fetchUserRole();
    await this.fetchCustomers();
    await this.fetchQuotes();
  },
  methods: {
    confirmBack() {
      this.backDialog = true;
    },
    async fetchUserRole() {
      try {
        const user = auth.currentUser;
        if (user) {
          const uid = user.uid;
          const userDoc = await getDoc(doc(db, "users", uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const role = userData.role || "";
            localStorage.setItem("role", role);
            this.userRole = role;
            // Assume quotes-specific permissions are stored under "quotes" in the user document.
            this.userPermissions = userData.quotes || {};
          }
        } else {
          this.userRole = "Guest";
          localStorage.setItem("role", this.userRole);
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
        this.snackbarMessage = "Failed to fetch user role.";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
    },
    async fetchCustomers() {
      try {
        const customersSnapshot = await getDocs(collection(db, "customers"));
        this.customers = customersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          companyName: doc.data()["Company Name"] || "",
          contactPerson: doc.data()["Contact Person"] || "",
        }));
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    },
    async fetchQuotes() {
      try {
        const quotesRef = collection(db, "EngineerQuotes");
        const quotesQuery = query(quotesRef, orderBy("quoteNumber", "desc"));
        const querySnapshot = await getDocs(quotesQuery);
        const quotesList = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const customer = this.customers.find(
            (customer) => customer.docId === data.customerName
          );
          quotesList.push({
            id: docSnap.id,
            quoteNumber: data.quoteNumber || "N/A",
            jobName: data.jobName || "N/A",
            customerName: customer
              ? customer.companyName
              : data.customerName || "Unknown Customer",
            customerNumber: data.customerNumber || "N/A",
            engineer: data.engineer || "N/A",
            technician: data.technician || "N/A",
            salesperson: data.salesperson || "N/A",
            status: data.status || "Job Order",
            urgency: data.urgency || "N/A",
            timeToComplete: data.timeToComplete || "N/A",
            setRate: data.setRate || "N/A",
            quoteNumberAdditional: data.quoteNumberAdditional || "N/A",
            salesOrderNumber: data.salesOrderNumber || "N/A",
            invoiceNumber: data.invoiceNumber || "N/A",
            cancellationReason: data.cancellationReason || "N/A",
            labourItems: data.labourItems || [],
            stockItems: data.stockItems || [],
            supplierQuotes: data.supplierQuotes || [],
            TotalLead: data.TotalLead || "N/A",
            ShaftSize: data.ShaftSize || "N/A",
            comment: data.comment || "N/A",
            grandTotal: data.grandTotal || "0.00",
            labourSubTotal: data.labourSubTotal || "0.00",
            stockSubTotal: data.stockSubTotal || "0.00",
            sampleNumber: data.sampleNumber || "N/A",
            customerReference: data.customerReference || "N/A",
            dateQuoted: data.dateQuoted || "N/A",
            currentDate: data.currentDate || "N/A",
            extraInfoFields: data.extraInfoFields || [],
            jobDescription: data.jobDescription || "N/A",
          });
        });
        this.quotes = quotesList;
      } catch (error) {
        console.error("Error fetching quotes:", error);
      }
    },
    navigateToFunctions() {
      this.backDialog = false;
      this.$router.push("/Functions");
    },
    navigateToEditQuote(quote) {
      // Since the edit button is hidden if not permitted,
      // no need to show an error here.
      this.$router.push({ name: "EditQuote", params: { quoteId: quote.id } });
    },
    handleAddQuoteClick() {
      // Since the add quote button is hidden if not permitted,
      // simply navigate.
      this.$router.push({ name: "AddQuote" });
    },
    loadMore() {
      this.visibleCount += this.loadIncrement;
    },
    resetPagination() {
      this.visibleCount = this.loadIncrement;
    },
    openExtraInfo(quote) {
      this.extraInfo = { ...quote };
      this.extraInfoDialog.visible = true;
    },
    closeExtraInfoDialog() {
      this.extraInfoDialog.visible = false;
    },
    openViewDialog(imageUrl) {
      window.open(imageUrl, "_blank");
    },
    getChipColor(value) {
      // You can define colors for lead time chips if needed.
      return "grey";
    },
    computeDaysSinceQuoted(dateStr) {
      if (!dateStr || dateStr === "N/A") return "N/A";
      const quoteDate = new Date(dateStr);
      if (isNaN(quoteDate)) return "N/A";
      const currentDate = new Date();
      const diffMs = currentDate - quoteDate;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    showSnackbar(message) {
      this.snackbarMessage = message;
      this.snackbar = true;
    },
    /**
     * Copy quote card content to clipboard.
     */
    copyCardContent(quote) {
      const leadTime = (quote.stockItems && quote.stockItems.length && quote.stockItems[0].leadTime) || "N/A";
      const content = `Quote Number: ${quote.quoteNumber || "N/A"}
Lead Time: ${leadTime}
Customer Name: ${quote.customerName || "N/A"}
Seal Type: ${quote.jobName || "N/A"}
Shaft Size: ${quote.ShaftSize || "N/A"}
Days Since Quoted: ${this.computeDaysSinceQuoted(quote.currentDate)}
Grand Total: ${quote.grandTotal || "0.00"}`;
      navigator.clipboard.writeText(content)
        .then(() => {
          this.showSnackbar("Card content copied successfully.");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          this.showSnackbar("Failed to copy card content.");
        });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

body,
div,
span,
v-card-title,
v-card-subtitle,
v-card-text,
v-row,
v-col,
v-card,
.v-chip,
.v-btn {
  font-family: "Montserrat", sans-serif;
}

.main-container {
  background-color: #121212;
  min-height: 100vh;
}

.header-row {
  background-color: #1e1e1e;
  border-bottom: 1px solid #333;
}

.modern-card {
  background-color: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  margin: 8px;
  box-shadow: 10px 10px 20px rgba(0, 0, 0, 0.2),
    -10px -10px 20px rgba(0, 0, 0, 0.1);
}

.modern-card:hover {
  transform: translateY(-5px);
  box-shadow: 10px 10px 25px rgba(0, 0, 0, 0.3),
    -10px -10px 25px rgba(0, 0, 0, 0.2);
}

.status-chip,
.quote-number-chip {
  font-weight: bold;
}

.section-title {
  font-size: 1.2rem;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.white-search-field .v-input__control {
  background-color: white;
  border-radius: 8px;
}

.white-search-field .v-label {
  color: black;
}

.white-search-field .v-input__icon--append-inner .v-icon {
  color: black;
}

.add-job-button {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.add-job-button:hover {
  background-color: #e63946;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.back-button {
  color: white;
  border-radius: 50%;
  transition: transform 0.3s ease-in-out;
}

.back-button:hover {
  transform: scale(1.1);
}

.back-button .v-icon {
  color: white;
}

.load-more-button {
  font-weight: 600;
  margin-top: 24px;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.load-more-button:hover {
  background-color: #e63946;
  transform: scale(1.02);
}

.text-caption {
  font-size: 0.8rem;
  color: #757575;
}

.font-weight-medium {
  font-weight: 500;
  color: #333;
}

.loader-row {
  margin-top: 50px;
}
</style>
